import { Ban, Download, LoaderCircle } from "lucide-react";
import type { ExportJobSummary } from "@signum/api-contracts";
import { DateTime, Empty, StatusBadge } from "./ui.js";

export const activeExportStatuses = new Set([
  "QUEUED", "COLLECTING_DATA", "CALCULATING", "GENERATING_CSV",
]);

export function RecentExports({
  jobs, busyId, includePrevious, onIncludePreviousChange, onDownload, onCancel,
}: {
  jobs: ExportJobSummary[];
  busyId?: string;
  includePrevious: boolean;
  onIncludePreviousChange(value: boolean): void;
  onDownload(job: ExportJobSummary): void;
  onCancel(job: ExportJobSummary): void;
}) {
  return <section className="panel compact-table recent-exports">
    <div className="panel-title"><div><span className="eyebrow">Reusable research files</span><h2>Recent exports</h2></div><div className="export-history-actions"><label className="history-toggle"><input type="checkbox" checked={includePrevious} onChange={(event) => onIncludePreviousChange(event.target.checked)}/><span>Include previous policies</span></label><span className="export-retention">Files remain available for 30 days</span></div></div>
    {jobs.length ? <div className="table-scroll"><table><thead><tr><th>Requested</th><th>Date range</th><th>Expanded data</th><th>Status</th><th>Progress</th><th>Result</th></tr></thead>
      <tbody>{jobs.map((job) => {
        const active = activeExportStatuses.has(job.status);
        const progress = job.totalCandidates
          ? `${job.processedCandidates} / ${job.totalCandidates} candidates`
          : job.currentStage ?? "Waiting";
        return <tr key={job.exportJobId}>
          <td><strong><DateTime value={job.createdAt}/></strong><small>{job.scope === "all" ? "All candidates" : "Recommendations only"} · {job.strategyVersion ?? "legacy policy"}</small></td>
          <td><strong>{job.startDate}</strong><small>through {job.endDate}</small></td>
          <td><strong>{job.expansionColumns.length} columns</strong><small>{expansionSummary(job)}</small></td>
          <td><StatusBadge status={job.status}/></td>
          <td className="wide-cell">{active && <LoaderCircle className="spin-icon" size={13}/>}<span>{progress}</span>{job.errorSummary && <small className="text-negative">{job.errorSummary}</small>}{job.warningCount > 0 && <small className="text-warning">{job.warningCount} provider data gaps</small>}</td>
          <td>{job.status === "READY"
            ? <button className="button secondary compact" disabled={busyId === job.exportJobId} onClick={() => onDownload(job)}><Download size={14}/> Download CSV</button>
            : active
              ? <button className="button secondary compact danger-button" disabled={busyId === job.exportJobId} onClick={() => onCancel(job)}><Ban size={14}/> Cancel</button>
              : <span className="export-unavailable">{job.status === "FAILED" ? "Review error" : "Unavailable"}</span>}</td>
        </tr>;
      })}</tbody></table></div>
      : <Empty title="No saved exports">Use Export CSV to prepare a reusable research file.</Empty>}
  </section>;
}

function expansionSummary(job: ExportJobSummary): string {
  const labels: string[] = [];
  if (job.expansionColumns.some((item) => [
    "enterprise_value", "ebitda_ttm", "free_cash_flow_growth",
    "total_debt", "stockholders_equity",
  ].includes(item))) labels.push("fundamentals");
  if (job.expansionColumns.some((item) => item.includes("return") || item.includes("52_week"))) {
    labels.push("price factors");
  }
  if (job.expansionColumns.includes("weighted_average_purchase_price")) labels.push("trade detail");
  return labels.join(", ") || "selected fields";
}
